import React from 'react';

export interface FeedbackEmptyProps {
  title: string;
  description?: string;
  /** Usually the same primary CTA as the page footer. */
  action?: React.ReactNode;
  /**
   * Custom illustration node. `false` hides it. Default: the shared
   * `/images/no-results.png` asset (same one TableData used before).
   */
  illustration?: React.ReactNode | false;
  /** `md` (default) is 148px at `sm+`; `sm` is 96px. Below `sm` always 96px. */
  size?: 'sm' | 'md';
  className?: string;
}

const illustrationSize: Record<
  NonNullable<FeedbackEmptyProps['size']>,
  string
> = {
  sm: 'w-24 h-24',
  md: 'w-24 h-24 sm:w-[148px] sm:h-[148px]',
};

/** Centered empty state (Bahasa Indonesia copy is the caller's job). */
export const FeedbackEmpty: React.FC<FeedbackEmptyProps> = ({
  title,
  description,
  action,
  illustration,
  size = 'md',
  className = '',
}) => {
  const illustrationNode =
    illustration === false ? null : (
      <div
        data-testid="feedback-empty-illustration"
        className={`mx-auto mb-4 ${illustrationSize[size]}`}
      >
        {illustration ?? (
          <img
            data-testid="feedback-empty-image"
            src="/images/no-results.png"
            alt=""
            className="h-full w-full object-contain"
          />
        )}
      </div>
    );

  return (
    <div
      role="status"
      className={`flex flex-col items-center text-center ${className}`.trim()}
    >
      {illustrationNode}
      <h3 className="text-[18px] font-medium leading-6 text-gray-900">
        {title}
      </h3>
      {description && (
        <p className="mt-2 max-w-prose text-sm leading-5 text-gray-600">
          {description}
        </p>
      )}
      {action && (
        <div className="mt-4 flex w-full justify-center [&>*]:w-full sm:[&>*]:w-auto">
          {action}
        </div>
      )}
    </div>
  );
};

export default FeedbackEmpty;
